import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import './PartnerReferalRewardModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import {API_BASE} from '../../../utils/apiConfig';
import {applyPartnerReferalRewardsClaim, setAvailableFactions} from '../../../redux/actions';
import {getPartnerRewardDisplayProps} from '../../../utils/partner/getPartnerRewardDisplayProps';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';
import {INVENTORY_ITEMS} from "../../../constants/inventoryItems";

const PAGE_ID = 'PartnerReferalRewardModal';

/**
 * Модалка реферальной награды (главное меню): показывается зарегистрированным
 * по ссылке партнера, пока награды не забраны
 * (auth.hasReceivedPartnerReferalRewards === false). Приоритетнее модалки
 * ежедневной награды — та открывается только после закрытия этой.
 * Клик по нику партнера забирает награды И ведет на его профиль (новичок
 * может посмотреть стату и историю матчей партнера).
 *
 * Переиспользуется программой «Пригласи друга» (та же модалка, но офер/клейм
 * и экшен клейма — дружеские): offerEndpoint/claimEndpoint/applyClaimAction
 */
function PartnerReferalRewardModal({
                                     onClose,
                                     onOpenPartnerProfile,
                                     onUnavailable,
                                     offerEndpoint = '/partner-referal-offer',
                                     claimEndpoint = '/partner-referal-rewards/claim',
                                     applyClaimAction = applyPartnerReferalRewardsClaim,
                                   }) {
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [offer, setOffer] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const res = await fetch(`${API_BASE}${offerEndpoint}`, {credentials: 'include'});
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Реферальная награда недоступна');
        }
        setOffer(data);
        setTimeout(() => setVisible(true), 50);
      } catch (e) {
        // Партнер удален/данные недоступны — модалка не показывается, меню
        // продолжает обычный сценарий (ежедневная награда)
        console.error('Partner referal offer error', e);
        onUnavailable?.();
      }
    };
    fetchOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const claimRewards = async () => {
    const res = await fetch(`${API_BASE}${claimEndpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: {'Content-Type': 'application/json'},
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Не удалось получить награды');
    }
    dispatch(applyClaimAction({
      numens: data.numens,
      crystals: data.crystals,
      premiumExpiresAt: data.premiumExpiresAt,
      hasNewItems: data.hasNewItems,
    }));

    // Начисленную фракцию экшен клейма в стор не заносит: список доступных
    // фракций (auth.availableFactions) грузится один раз при авторизации, и
    // «Моя коллекция» берет фракции именно оттуда — в отличие от сундуков и
    // обличий, которые она перезапрашивает при открытии. Без этого обновления
    // фракция появлялась бы в коллекции только после перезагрузки страницы,
    // хотя на сервере уже начислена
    const hasFactionReward = (offer?.rewards || []).some(
      (reward) => reward.itemType === INVENTORY_ITEMS.FACTION
    );
    if (hasFactionReward) {
      try {
        const factionsRes = await fetch(`${API_BASE}/available-factions`, {credentials: 'include'});
        const factionsData = await factionsRes.json().catch(() => ({}));
        if (factionsRes.ok) {
          dispatch(setAvailableFactions(factionsData.factions || []));
        }
      } catch (e) {
        // Награды уже начислены — сбой обновления списка не должен ронять
        // клейм; фракция подтянется при следующей загрузке профиля
        console.error('Refresh available factions after referal claim error', e);
      }
    }

    MENU_SOUND_MANAGER.playCoins(data.numens);
  };

  const handleClaimClick = async () => {
    if (claiming) return;
    setClaiming(true);
    setError(null);
    try {
      await claimRewards();
      MENU_SOUND_MANAGER.playChestOpen();
      handleClose();
    } catch (e) {
      console.error('Claim partner referal rewards error', e);
      setError(e.message || 'Не удалось получить награды');
    } finally {
      setClaiming(false);
    }
  };

  const handlePartnerNameClick = async () => {
    if (claiming) return;
    setClaiming(true);
    setError(null);
    try {
      await claimRewards();
      onOpenPartnerProfile?.(offer.partnerUserId, offer.partnerName);
    } catch (e) {
      console.error('Claim partner referal rewards error', e);
      setError(e.message || 'Не удалось получить награды');
    } finally {
      setClaiming(false);
    }
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && !claiming) {
      handleClose();
    }
  };

  if (!offer) return null;

  return (
    <div
      className={`partner-referal-reward-overlay ${visible ? 'partner-referal-reward-overlay_visible' : ''}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className={`partner-referal-reward-modal ${visible ? 'partner-referal-reward-modal_visible' : ''}`}>
        <h2 className="partner-referal-reward-title">Поздравляем!</h2>

        <p className="partner-referal-reward-description">
          Вы получили награды за регистрацию по реферальной ссылке игрока{' '}
          <button
            type="button"
            className="partner-referal-reward-partner-name"
            onClick={handlePartnerNameClick}
            disabled={claiming}
            title="Забрать награды и открыть профиль партнера"
          >
            {offer.partnerName}
          </button>
        </p>

        <div className="partner-referal-reward-list">
          {offer.rewards.map((reward, index) => {
            const display = getPartnerRewardDisplayProps(reward);
            let descriptionText = '';
            if (display.itemType === INVENTORY_ITEMS.CRYSTALS) {
              descriptionText = 'кристаллов'
            } else if (display.itemType === INVENTORY_ITEMS.NUMENS) {
              descriptionText = 'нуменов'
            }
            return (
              <div
                key={index}
                className={`partner-referal-reward-item ${display.colorClass} partner-form-reward-preview_type-${display.itemType}`}
              >
                {display.iconSrc && (
                  <img className="partner-referal-reward-item-icon" src={display.iconSrc} alt=""/>
                )}
                <span className="partner-referal-reward-item-label">{display.fullLabel + ' ' + descriptionText}</span>
              </div>
            );
          })}
        </div>

        {error && <p className="partner-referal-reward-error">{error}</p>}

        <div className="main-menu-buttons partner-referal-reward-buttons">
          <PageButton
            onClick={handleClaimClick}
            pageId={PAGE_ID}
            buttonId="claim"
            disabled={claiming}
          >
            {claiming ? 'Получение...' : 'Забрать дар'}
          </PageButton>
        </div>
      </div>
    </div>
  );
}

export default PartnerReferalRewardModal;
